import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '../lib/api-client'
import { useReturnDestination, withReturnPath } from '../lib/returnPath'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Logo } from '../components/shell/Logo'

/** Shared-platform password recovery; no local password/token mechanism is invented. */
export function PasswordRecoveryPage({ reset = false }: { reset?: boolean }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const returnTo = useReturnDestination()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = reset ? !!token && password.length >= 8 && password === confirm : email.trim().length > 0
  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!valid || busy) return
    setBusy(true); setError(null)
    try {
      if (reset) await apiClient.resetPassword(token, password)
      else await apiClient.requestPasswordReset(email.trim())
      setSent(true)
    } catch (err) { setError(err instanceof Error ? err.message : 'That request could not be completed. Please try again.') }
    finally { setBusy(false) }
  }
  return <div className="flex min-h-screen items-center justify-center bg-paper-page px-5 py-8"><div className="w-full max-w-[440px] rounded-card border border-line bg-paper-surface p-6 shadow-card">
    <Logo /><h1 className="mt-5 font-serif text-[28px]">{reset ? 'Choose a new password' : 'Reset your password'}</h1>
    {sent ? <p role="status" className="mt-4 text-sm text-moss">{reset ? 'Password reset. Sign in with your new password.' : 'If an account exists with this email, you will receive password reset instructions.'}</p> : reset && !token ? <p role="alert" className="mt-4 text-sm text-honey-fg">This reset link is missing its token. Request a new link.</p> : <form className="mt-5 grid gap-4" onSubmit={submit}>
      {reset ? <><Input label="New password" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /><Input label="Confirm new password" type="password" autoComplete="new-password" required value={confirm} onChange={(event) => setConfirm(event.target.value)} /><p className="text-xs text-ink-muted">Use at least 8 characters. The platform may require a stronger password.</p></> : <Input label="Email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />}
      {error && <p role="alert" className="text-sm text-honey-fg">{error}</p>}
      <Button type="submit" disabled={!valid || busy}>{busy ? 'Working…' : reset ? 'Save new password' : 'Send reset instructions'}</Button>
    </form>}
    <div className="mt-5 flex flex-wrap gap-4 text-sm"><Link to={withReturnPath('/login', returnTo)}>Back to sign in</Link>{reset && <Link to={withReturnPath('/forgot-password', returnTo)}>Request a new reset link</Link>}</div>
  </div></div>
}
